// Store locator (sections/store-locator.liquid).
// Finds a starting point from the visitor's typed location (looked up with OpenStreetMap's
// Nominatim service) or from the browser's location, then sorts the stores nearest first and
// shows how far away each one is. Stores without coordinates stay at the end of the list.
if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    class StoreLocator extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.store-locator__form');
        this.input = this.querySelector('.store-locator__input');
        this.locateButton = this.querySelector('[data-locate]');
        this.status = this.querySelector('[data-status]');
        this.list = this.querySelector('[data-list]');
        this.stores = Array.from(this.querySelectorAll('[data-store]'));
        if (!this.form || !this.list) return;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.searchAddress(this.input.value.trim());
        });
        this.locateButton?.addEventListener('click', () => this.useCurrentLocation());
      }

      setStatus(message) {
        this.status.textContent = message;
      }

      setBusy(busy) {
        this.toggleAttribute('data-busy', busy);
        this.locateButton.disabled = busy;
      }

      async searchAddress(query) {
        if (!query) {
          this.setStatus('Enter a city, ZIP code or address.');
          this.input.focus();
          return;
        }

        this.setBusy(true);
        this.setStatus('Searching…');
        try {
          const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Lookup failed: ${response.status}`);
          const [place] = await response.json();
          if (!place) {
            this.setStatus(`We couldn't find "${query}". Try a city, ZIP code or full address.`);
            return;
          }
          this.sortFrom(Number(place.lat), Number(place.lon), query);
        } catch (error) {
          this.setStatus('Location search is unavailable right now. Please try again in a moment.');
        } finally {
          this.setBusy(false);
        }
      }

      useCurrentLocation() {
        if (!('geolocation' in navigator)) {
          this.setStatus("Your browser can't share its location. Try searching by city or ZIP code.");
          return;
        }

        this.setBusy(true);
        this.setStatus('Finding your location…');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.setBusy(false);
            this.input.value = '';
            this.sortFrom(position.coords.latitude, position.coords.longitude, 'your location');
          },
          (error) => {
            this.setBusy(false);
            this.setStatus(
              error.code === error.PERMISSION_DENIED
                ? 'Location access was blocked. You can search by city or ZIP code instead.'
                : "We couldn't get your location. Try searching by city or ZIP code."
            );
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      }

      sortFrom(lat, lng, label) {
        const withDistance = this.stores.map((store) => {
          const storeLat = parseFloat(store.dataset.lat);
          const storeLng = parseFloat(store.dataset.lng);
          const known = Number.isFinite(storeLat) && Number.isFinite(storeLng);
          return { store, miles: known ? this.milesBetween(lat, lng, storeLat, storeLng) : Infinity };
        });

        withDistance.sort((a, b) => a.miles - b.miles);
        withDistance.forEach(({ store, miles }) => {
          const distance = store.querySelector('[data-distance]');
          if (distance) {
            distance.hidden = !Number.isFinite(miles);
            distance.textContent = Number.isFinite(miles) ? `${this.formatMiles(miles)} away` : '';
          }
          this.list.appendChild(store);
        });

        const nearest = withDistance[0];
        if (nearest && Number.isFinite(nearest.miles)) {
          const name = nearest.store.querySelector('.store-locator__store-name')?.textContent.trim();
          this.setStatus(`Stores nearest ${label}. Closest: ${name}, ${this.formatMiles(nearest.miles)} away.`);
        } else {
          this.setStatus(`Showing all stores.`);
        }
      }

      formatMiles(miles) {
        return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles).toLocaleString()} mi`;
      }

      // Great-circle distance (haversine), in miles.
      milesBetween(lat1, lng1, lat2, lng2) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const dLat = toRadians(lat2 - lat1);
        const dLng = toRadians(lng2 - lng1);
        const a =
          Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
        return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }
    }
  );
}
